import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import {
  ProductScreen, Appbar, Summary, Chips, EmptyState, Sheet, DetailRow, Pill, Mono, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { auditDate, getAuditDashboard, moduleLabel, statusMeta } from "./auditMobileApi";
import useAuditScope from "../../gestion-audit/useAuditScope";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
} from "../../gestion-audit/auditRequestGate";

const periods = [{ key: 1, label: "Aujourd'hui" }, { key: 7, label: "7 jours" }, { key: 30, label: "30 jours" }, { key: 90, label: "90 jours" }];
const scopeOptions = [{ key: "company", label: "Entreprise" }, { key: "all", label: "Toutes" }];

export default function AuditDashboardMobile() {
  const onMenu = useMobileMenu();
  const {
    auditScope,
    companyId,
    isSuperAdmin,
    scope: effectiveAuditScope,
    scopeKey: auditScopeKey,
    setAuditScope,
  } = useAuditScope();
  const [period, setPeriod] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => { latestAuditScopeKeyRef.current = auditScopeKey; }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) requestGateRef.current = createAuditRequestGate();

  const load = useCallback(async () => {
    const request = requestGateRef.current.start(auditScopeKey);
    setLoading(true);
    try {
      const nextData = await getAuditDashboard(period, companyId, {
        scope: effectiveAuditScope,
        signal: request.signal,
      });
      if (request.isCurrent(latestAuditScopeKeyRef.current)) setData(nextData);
    } catch (error) {
      if (isAuditRequestCanceled(error, request.signal) || !request.isCurrent(latestAuditScopeKeyRef.current)) return;
      message.error(error.message || "Impossible de charger le journal d'audit");
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) setLoading(false);
    }
  }, [auditScopeKey, companyId, effectiveAuditScope, period]);
  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;
    if (scopeChanged) {
      requestGateRef.current.cancel();
      setData(null);
      setDetail(null);
    }
    load();
  }, [auditScopeKey, load]);
  useEffect(() => () => requestGateRef.current?.cancel(), []);

  const stats = data?.global_stats || {};
  const successRate = useMemo(() => {
    const total = Number(stats.total_logs || 0);
    return total ? ((Number(stats.success_count || 0) / total) * 100).toFixed(1) : "0.0";
  }, [stats.success_count, stats.total_logs]);
  const topModules = data?.top_modules || [];
  const topUsers = data?.top_users || [];
  const timeline = data?.activity_timeline || [];
  const critical = data?.critical_actions || [];

  return <ProductScreen>
    <Appbar title="Journal d'Audit" subtitle={`Activité sur ${period === 1 ? "aujourd'hui" : `${period} jours`}`} onMenu={onMenu} action={<button className="iconbtn" aria-label="Actualiser" onClick={load}><Ico name="refresh-cw" /></button>} />
    <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
      <Summary items={[{ label: "Actions", value: fmt(stats.total_logs || 0) }, { label: "Utilisateurs", value: fmt(stats.active_users || 0) }, { label: "Succès", value: `${successRate}%` }]} />
      {isSuperAdmin ? <><div className="field__lbl">Périmètre</div><Chips value={auditScope} onChange={(value) => setAuditScope(value || "company")} options={scopeOptions} /></> : null}
      <Chips value={period} onChange={setPeriod} options={periods} />
    </div>
    <div className="body">{loading && !data ? <EmptyState loading title="Chargement du tableau de bord…" /> : !data ? <EmptyState icon="activity" title="Données indisponibles" /> : <div className="list">
      <div className="field__lbl">Activité récente</div>
      {timeline.length ? timeline.slice(-7).reverse().map((item, index) => <div className="rcard" key={`${item.period}-${index}`}><Mono name={String(item.period || "A")} square /><div className="rcard__main"><div className="rcard__name">{auditDate(item.period)}</div><div className="rcard__meta"><Pill tone="info" label={`${fmt(item.total_actions || 0)} actions`} /><span className="rcard__sub">{fmt(item.success_count || 0)} succès</span></div></div></div>) : <EmptyState icon="activity" title="Aucune activité" />}

      <div className="field__lbl" style={{ marginTop: 14 }}>Modules les plus actifs</div>
      {topModules.slice(0, 6).map((item) => <div className="rcard" key={item.module}><Mono name={moduleLabel(item.module)} square /><div className="rcard__main"><div className="rcard__name">{moduleLabel(item.module)}</div><div className="rcard__meta"><Pill tone="info" label={`${fmt(item.total_actions || 0)} actions`} /><span className="rcard__sub">{Number(item.success_rate || 0).toFixed(1)}% de succès</span></div></div></div>)}

      <div className="field__lbl" style={{ marginTop: 14 }}>Utilisateurs les plus actifs</div>
      {topUsers.slice(0, 5).map((item) => <div className="rcard" key={item.user_id || item.username}><Mono name={item.username || "Utilisateur"} /><div className="rcard__main"><div className="rcard__name">{item.username || "Utilisateur inconnu"}</div><div className="rcard__meta"><Pill tone="ok" label={`${fmt(item.total_actions || 0)} actions`} /><span className="rcard__sub">{Number(item.avg_success_rate || 0).toFixed(1)}% de succès</span></div></div></div>)}

      <div className="field__lbl" style={{ marginTop: 14 }}>Actions critiques</div>
      {!critical.length ? <EmptyState icon="shield-check" title="Aucune action critique" /> : critical.slice(0, 10).map((item) => { const meta = statusMeta(item.status); return <button className="rcard" key={item.id} onClick={() => setDetail(item)}><Mono name={item.username || item.module || "S"} square /><div className="rcard__main"><div className="rcard__name">{item.action_type || "Action"} · {moduleLabel(item.module)}</div><div className="rcard__meta"><Pill tone={meta[1]} label={meta[0]} /><span className="rcard__sub">{item.username || "Système"} · {auditDate(item.created_at)}</span></div></div><span className="rcard__chev"><Ico name="chevron-right" /></span></button>; })}
    </div>}</div>
    <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title="Action critique" subtitle={detail ? auditDate(detail.created_at, true) : ""}>{detail ? <><DetailRow label="Utilisateur" value={detail.username || (detail.user_id ? `#${detail.user_id}` : "Système")} /><DetailRow label="Module" value={moduleLabel(detail.module)} /><DetailRow label="Action" value={detail.action_type || "—"} /><DetailRow label="Statut" value={statusMeta(detail.status)[0]} /><DetailRow label="Ressource" value={detail.resource_type ? `${detail.resource_type} #${detail.resource_id || "—"}` : "—"} /><DetailRow label="Adresse IP" value={detail.ip_address || "—"} /></> : null}</Sheet>
  </ProductScreen>;
}
